import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  clearModuleNavigation,
  ModuleNavigationType,
  removeModuleNavigation,
} from 'services/moduleNavigation';
import { ModuleNavigation } from 'app/components/AppBar/components';
import { Routes } from '../../navigation/routes';
import { useUrlQueryObject } from 'services/url';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  TransferOrderUrlQueryObject,
  TransferOrder,
} from 'services/transferOrders/types';
import { showNotification } from 'services/api';
import { PaperSlidingLayout } from 'ui/components/Paper/PaperSlidingLayout';
import TransferOrderSearchResults from './components/TransferOrderSearchResults/TransferOrderSearchResults';
import { TransferOrderDetailsCard } from './components/TransferOrderDetailsCard';

const NEW_TRANSFER_ORDER_ID = -1 as const;

interface TransferOrdersPageComponent extends React.FunctionComponent {
  route: typeof Routes.TransferOrdersPage;
}

const TransferOrdersPage: React.FC = () => {
  const dispatch = useDispatch();
  const [bypassRouteLeavingGuard, setBypassRouteLeavingGuard] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [urlQuery, extendUrlQuery] =
    useUrlQueryObject<TransferOrderUrlQueryObject>(navigate, location);

  const activeTransferOrderId = urlQuery.activeId || null;

  const onAddTransferOrderClick = useCallback(() => {
    extendUrlQuery({ activeId: NEW_TRANSFER_ORDER_ID });
  }, [extendUrlQuery]);

  const handleTransferOrderClick = useCallback(
    (id: number) => {
      extendUrlQuery({ activeId: id });
    },
    [extendUrlQuery]
  );

  const handleCloseDetails = useCallback(() => {
    setBypassRouteLeavingGuard(false); // Reset bypass flag when closing
    extendUrlQuery({ activeId: undefined });
  }, [extendUrlQuery]);

  const handleDetailsSuccess = useCallback((transferOrder: TransferOrder) => {
    showNotification(
      `Transfer order ${transferOrder.number} created successfully`,
      {
        variant: 'success',
      }
    );

    // TODO: Refresh the transfer orders list
    // This will be implemented when the list component is updated to handle refreshes
  }, []);

  useEffect(() => {
    dispatch(clearModuleNavigation(ModuleNavigationType.Materials));

    return () => {
      dispatch(removeModuleNavigation());
    };
  }, [dispatch]);

  return (
    <>
      <ModuleNavigation />
      <PaperSlidingLayout shown={activeTransferOrderId !== null}>
        <TransferOrderSearchResults
          handleAddNewClick={onAddTransferOrderClick}
          activeTransferOrderId={activeTransferOrderId}
          onTransferOrderClick={handleTransferOrderClick}
        />
        {activeTransferOrderId !== null && (
          <TransferOrderDetailsCard
            transferOrderId={activeTransferOrderId}
            onClose={handleCloseDetails}
            onSuccess={handleDetailsSuccess}
            bypassRouteLeavingGuard={bypassRouteLeavingGuard}
            setBypassRouteLeavingGuard={setBypassRouteLeavingGuard}
          />
        )}
      </PaperSlidingLayout>
    </>
  );
};

(TransferOrdersPage as TransferOrdersPageComponent).route =
  Routes.TransferOrdersPage;

export default TransferOrdersPage as TransferOrdersPageComponent;
